import { useState } from "react";
import Modal from "../Modal/Modal";
import { ParseOctgnXmlResult, parseOctgnXml } from "../../pages/DeckBuilderPage/OctgnXmlGenerator";
import { Spinner } from "../Spinner/Spinner";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { AppState } from "../../appSlice";
import WarningAlert from "../Alerts/WarningAlert";
import { Button } from "../Control/Button/Button";
import deepEquals from "fast-deep-equal";
import { InfoAlert } from "../Alerts/InfoAlert";

export type ImportFromOctgnProps = {
    onProcess: (result: ParseOctgnXmlResult) => void,
    onReset?: () => void
}

export function ImportFromOctgn(props: ImportFromOctgnProps) {
    const [isParsing, setIsParsing] = useState<boolean>(false);
    const [parseError, setParseError] = useState<string>("");
    const [parseResult, setParseResult] = useState<ParseOctgnXmlResult>();
    const [totalCards, setTotalCards] = useState(0);

    const {
        variantCache
    } = useSelector<RootState, AppState>(x => x.app);

    const handleFileChosen = (files: FileList | null) => {
        if (!files || files.length < 1) {
            return;
        }

        setIsParsing(true);
        const file = files[0];

        const fileReader = new FileReader();
        fileReader.onloadend = () => handleFileRead(fileReader);
        fileReader.readAsText(file);
    }

    const handleFileRead = (fileReader: FileReader) => {
        const content = fileReader.result?.toString();
        if (!content) {
            setIsParsing(false);
            return;
        }

        const lines = (String(content).match(/\n/g) || '').length + 1
        const maxImportSize = 30000;

        if (lines > maxImportSize) {
            setIsParsing(false);
            alert(`The file provided contains ${lines} lines when the max allowed to be imported is ${maxImportSize}`);
            return;
        }

        const parseResult = parseOctgnXml(content, variantCache.any.all.list);

        let total = 0;
        Object.values(parseResult.variantQuantityDictionary).forEach((qty) => total = total + qty);

        let error = "";
        if (parseResult.missingCards.length > 0) {
            error = "Could not find the following cards:";
            parseResult.missingCards.forEach((missingCard) => {
                error = `${error}\n\n${missingCard.name}\n${missingCard.octgnId}`;
            });

            setParseError(error);
        }

        setParseResult(parseResult);
        setTotalCards(total);
        setIsParsing(false);
    }

    const processImport = () => {
        if (!parseResult) {
            return;
        }

        props.onProcess(parseResult);
        reset();
    }

    const reset = () => {
        setIsParsing(false);
        setParseResult(undefined);
        setTotalCards(0);
        setParseError("");

        if (props.onReset) {
            props.onReset();
        }
    }

    return (
        <>
            <div className="mt-8">
                <input
                    className="relative m-0 block w-full min-w-0 flex-auto rounded border border-solid border-color-border bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-contrast-tertiary transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-tertiary file:px-3 file:py-[0.32rem] file:text-contrast-primary file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-tertiary/75 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 file:cursor-pointer"
                    type="file"
                    onChange={(event) => handleFileChosen(event.target.files)}
                    accept=".o8d"
                    id="formFile" />
            </div>

            {
                isParsing &&
                <div className="mt-8">
                    <Spinner />
                </div>
            }

            {
                (parseError) &&
                <div className="mt-8">
                    <WarningAlert text={parseError} />
                </div>
            }
            {
                (parseResult && !deepEquals(parseResult?.variantQuantityDictionary, {})) &&
                <div className={parseError ? `mt-2` : 'mt-8'}>
                    <WarningAlert classNames={{ container: "mb-2" }} text="Any cards in the current deck will be merged with the cards from this import." />
                    <InfoAlert>{`Import ${totalCards} cards?`}</InfoAlert>
                </div>
            }

            <div className="mt-8">
                <Button className="" onClick={() => processImport()} disabled={isParsing || deepEquals(parseResult?.variantQuantityDictionary, {})}>Import</Button>

                <Button
                    className="ml-2"
                    color="component"
                    onClick={() => {
                        reset();
                    }}
                >
                    Cancel
                </Button>
            </div>
        </>
    )
}

export type ImportFromOctgnModalProps = ImportFromOctgnProps & {
    isModalOpen: boolean,
    onModalOpenStateChange: (isOpen: boolean) => void
}

export function ImportFromOctgnModal(props: ImportFromOctgnModalProps) {
    return (
        <Modal isOpen={props.isModalOpen} onOpenChange={(newIsOpen) => {
            props.onModalOpenStateChange(newIsOpen);
        }} title="Import From OCTGN">
            <ImportFromOctgn
                onProcess={props.onProcess}
                onReset={() => props.onModalOpenStateChange(false)}/>
        </Modal>
    )
}